import React, { useRef, useMemo, useState, useCallback } from "react";
import MappedSelection from "./MappedSelection";
import { MarketButton } from "components-sbk/src/components/MarketButton/MarketButton";
import { Icon } from "components/src/components/Icon/Icon";
import cx from "components/src/utils/cx";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { useBrandTheme } from "shared/src/hooks/useBrandTheme";
import { GradientOverlay } from "components/src/components/Carousel/components/GradientOverlay";
import {
  MarketSimpleCarousel,
  type MarketSimpleCarouselOnActiveItemType,
} from "./MarketSimpleCarousel";

const PaddingSelection = () => {
  return <div className="cui-h-full" style={{width: 56}} />
}
PaddingSelection.isPadding = true;

export function MarketRowScrollableSelections({
  isDesktop,
  showNav,
  sliderInitialIndex,
  marketButtonData,
  hasScrolling,
  selections,
}) {
  const selLen = selections?.length;
  const generatedId = useIdRef({ str: `cui-market-template-scrollable-` });
  const brand = useBrandTheme();
  const placeholderType = brand === "wh" ? "branded-wh" : "branded-czr";
  const [currentActiveItem, setCurrentActiveItem] =
    useState<Parameters<MarketSimpleCarouselOnActiveItemType>[0]>({index: sliderInitialIndex, isBeginning: false, isEnd: false });
  const simpleCarouselRef = useRef<any>(null);

  // Calculate edge states using the same logic as MarketRowScrollable
  const selectionWidth = 56;

  // Enhanced click handler that uses carousel navigation
  const handleInteractionClick = (dir = "next") => {
    return () => {
      if (!simpleCarouselRef.current) return;
      // Use direct navigation to target index (already adjusted in the mapping)
      // simpleCarouselRef.current.goToIndex(targetIndex);
      if (dir === "next") {
        simpleCarouselRef.current.goToNext();
      } else {
        simpleCarouselRef.current.goToPrev();
      }
    };
  };

  const handleActiveItem = useCallback<MarketSimpleCarouselOnActiveItemType>(
    (currentState) => {
      setCurrentActiveItem(currentState);
    },
    []
  );

  const _selectionsWithPadding = useMemo(()=>{
    if (!isDesktop || hasScrolling) return selections;
    // const reversedSelections = [...selections].reverse();
    if (selLen === 1) return [PaddingSelection, PaddingSelection, PaddingSelection, ...selections, PaddingSelection]
    if (selLen === 2) return [PaddingSelection, PaddingSelection, ...selections, PaddingSelection]
    if (selLen === 3) return [PaddingSelection, ...selections, PaddingSelection]
  },[selections, isDesktop, hasScrolling, selLen]);

  const leftGradient = showNav ? (isDesktop ? 56 : 52) : 0; 
  const rightGradient = showNav ? 56 : 0;
  return (
    <>
      <Chevron
        hide={currentActiveItem.isBeginning || !hasScrolling}
        showNav={showNav}
        onClick={handleInteractionClick("prev")}
      />
      <GradientOverlay
        // className={cx({
        //   "cui-left-0": !isDesktop,
        //   "cui-left-[46px]": isDesktop,
        // })}
        forceFade={true}
        type={"light"}
        zIndex={9}
        visible={true}
        left={!showNav ? 0 : leftGradient }
      />
      <GradientOverlay
        slot="container-end"
        // className={cx({
        //   "cui-right-0": !isDesktop,
        //   "cui-right-[46px]": isDesktop,
        // })}
        forceFade={true}
        type={"light"}
        zIndex={9}
        visible={true}
        flip
        right={!showNav ? 0 : rightGradient }
      />
      <MarketSimpleCarousel
        ref={simpleCarouselRef}
        onActiveItem={handleActiveItem}
        hasScrolling={hasScrolling}
        initialIndex={sliderInitialIndex} // Adjust for alignment placeholders
        isDesktop={isDesktop}
        showNav={showNav}
        style={showNav && !isDesktop && currentActiveItem.isBeginning ? { marginLeft: -4 } : undefined}
      >
        {hasScrolling ? (
          <PlaceholderWrapper hasScrolling={hasScrolling} isDesktop={isDesktop}>
            <MarketButton
              isPlaceholder={true}
              placeholderType={placeholderType}
              id={`${generatedId.current}-1`}
              fluid={true}
            />
          </PlaceholderWrapper>
        ) : null}
        {_selectionsWithPadding?.map((selection, i) => {
          const adjustedIndex = i + 1; // Account for both regular and alignment placeholders
          const nextSelection = Math.min(currentActiveItem?.index + 1, selLen);
          const prevSelection = Math.max(currentActiveItem?.index - 1, 0);
          const activeSlides = isDesktop
            ? [
                prevSelection,
                currentActiveItem?.index,
                nextSelection,
              ]
            : [currentActiveItem?.index];

          return (
            <div style={{ width: selectionWidth }} key={`${generatedId.current}-${selection?.id}`}>
              { selection?.isPadding ? selection() : <MappedSelection
                index={i}
                selection={selection}
                marketButtonData={marketButtonData}
                isMuted={(selLen > 3 && isDesktop || !isDesktop) && !activeSlides.includes(adjustedIndex)}
                onInactiveClick={
                  !isDesktop ? handleInteractionClick(currentActiveItem?.index+1 === adjustedIndex ? "next" : "prev") : null
                }
              />}
            </div>
          );
        })}
        {hasScrolling ? (
          <PlaceholderWrapper hasScrolling={hasScrolling} isDesktop={isDesktop}>
            <MarketButton
              isPlaceholder={true}
              placeholderType={placeholderType}
              fluid={true}
              id={`${generatedId.current}-2`}
            />
          </PlaceholderWrapper>
        ) : null}
      </MarketSimpleCarousel>
      <Chevron
        dir="right"
        hide={currentActiveItem.isEnd || !hasScrolling}
        showNav={showNav}
        onClick={handleInteractionClick("next")}
      />
    </>
  );
}

const Chevron = ({
  dir = "left",
  show = true,
  hide = false,
  showNav = false,
  onClick,
}) => {
  return (
    <div
      className={`cui__market-template-scrollable-chevron cui-box-content cui-w-[44px] cui-min-w-[44px] cui-max-w-[44px] cd ..cui-items-center cui-justify-center ${dir === "left" ? "cui-mr-sm" : "cui-ml-sm"} ${
        hide && showNav
          ? "cui-opacity-0 cui-pointer-events-none"
          : "cui-cursor-pointer"
      } ${showNav ? "cui-flex" : "cui-hidden"}`}
      onClick={onClick}
    >
      <Icon
        name={`ui_chevron_${dir}`}
        size={20}
        className="cui-mx-auto"
        color="fg-moderate"
      />
    </div>
  );
};

const PlaceholderWrapper = ({ children, hasScrolling, isDesktop }) => {
  if (!hasScrolling) return null;
  return (
    <div
      className={cx(
        {
          "cui-hidden": isDesktop && !hasScrolling,
        },
        "cui-min-w-[56px] cui-h-full"
      )}
    >
      {children}
    </div>
  );
};

const InvisiblePlaceholder = () => (
  <div className="cui-min-w-[56px] cui-h-full cui-snap-center cui-opacity-0 cui-pointer-events-none">
    <div className="cui-w-full cui-h-full" />
  </div>
);
